import React, { useState, useEffect, useMemo } from "react";
import { Button, Table, Spinner, Badge } from "react-bootstrap";
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useNavigate } from "react-router-dom";

const PageList_VehicleTypeInstructionMaster = () => {
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const API_URL = `${API_WEB_URLS.MASTER}/0/token/VehicleTypeInstructionMaster`;

  const loadData = () => {
    setLoading(true);
    Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0").finally(() =>
      setLoading(false)
    );
  };

  useEffect(() => {
    loadData();
  }, [dispatch, API_URL]);

  const data = useMemo(() => gridData || [], [gridData]);

  const columns = useMemo(
    () => [
      {
        Header: "Vehicle Type",
        accessor: "VehicleTypeName",
        Cell: ({ value }) => (
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>
            {value || "All / Not Set"}
          </span>
        ),
      },
      {
        Header: "Configured Lines (Read before Booking)",
        id: "linesPreview",
        Cell: ({ row }) => {
          const r = row.original;
          const lines = [
            r.Line1, r.Line2, r.Line3, r.Line4, r.Line5,
            r.Line6, r.Line7, r.Line8, r.Line9, r.Line10,
          ].filter((l) => l && l.trim() !== "");

          if (lines.length === 0) {
            return <span className="text-muted" style={{ fontSize: 11 }}>No lines configured</span>;
          }

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {lines.slice(0, 3).map((l, idx) => (
                <div key={idx} style={{ fontSize: 11, color: "#444" }}>
                  <span style={{ color: "#E87B2D", fontWeight: "bold", marginRight: 4 }}>•</span>
                  {l}
                </div>
              ))}
              {lines.length > 3 && (
                <span style={{ fontSize: 10, color: "#888", fontStyle: "italic" }}>
                  +{lines.length - 3} more lines...
                </span>
              )}
            </div>
          );
        },
      },
      {
        Header: "Total Lines",
        id: "totalLines",
        Cell: ({ row }) => {
          const r = row.original;
          const count = [
            r.Line1, r.Line2, r.Line3, r.Line4, r.Line5,
            r.Line6, r.Line7, r.Line8, r.Line9, r.Line10,
          ].filter((l) => l && l.trim() !== "").length;

          return (
            <Badge bg={count > 0 ? "info" : "secondary"} style={{ fontSize: 11 }}>
              {count} / 10
            </Badge>
          );
        },
      },
      {
        Header: "Status",
        accessor: "IsActive",
        Cell: ({ value }) => (
          <Badge bg={value ? "success" : "danger"} style={{ fontSize: 10 }}>
            {value ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        id: "actions",
        Header: "Action",
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: 6 }}>
            <Button
              size="sm"
              variant="outline-primary"
              className="py-0 px-2"
              style={{ fontSize: 11, height: 24 }}
              onClick={() =>
                navigate("/AddVehicleTypeInstructionMaster", {
                  state: { Id: row.original.Id, F_VehicleType: row.original.F_VehicleType },
                })
              }
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              className="py-0 px-2"
              style={{ fontSize: 11, height: 24 }}
              onClick={() => {
                if (window.confirm("Delete instructions for this vehicle type?")) {
                  Fn_DeleteData(dispatch, row.original.Id, API_URL, API_URL + "/Id/0").then(
                    (r) => r && r.success && loadData()
                  );
                }
              }}
            >
              Del
            </Button>
          </div>
        ),
      },
    ],
    [dispatch, navigate, API_URL]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 15, sortBy: [{ id: "VehicleTypeName", desc: false }] },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (loading) {
    return (
      <div
        style={{
          padding: "16px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 180,
        }}
      >
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 12px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
          paddingBottom: 6,
          borderBottom: "1px solid #dee2e6",
        }}
      >
        <div>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#333" }}>
            Vehicle Instruction Master
          </span>
          <p style={{ margin: 0, fontSize: 11, color: "#666" }}>
            Configure up to 10 lines displayed under &quot;Read before Booking&quot; in Customer App for each Vehicle Type.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="text"
            placeholder="Search..."
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="form-control form-control-sm"
            style={{ width: 160, height: 28, fontSize: 12 }}
          />
          <Button
            size="sm"
            variant="primary"
            className="py-0 px-2"
            style={{ fontSize: 12, height: 28 }}
            onClick={() => navigate("/AddVehicleTypeInstructionMaster", { state: { Id: 0 } })}
          >
            + Add Instruction
          </Button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", marginBottom: 6 }}>
        <Table
          {...getTableProps()}
          size="sm"
          bordered
          hover
          style={{ fontSize: 12, minWidth: 750, background: "#fff" }}
        >
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map((col, j) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    key={j}
                    style={{
                      padding: "6px 8px",
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: "#E87B2D",
                      color: "#fff",
                    }}
                  >
                    {col.render("Header")}
                    {col.isSorted && (
                      <span style={{ marginLeft: 3 }}>{col.isSortedDesc ? "▼" : "▲"}</span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4 text-muted" style={{ fontSize: 13 }}>
                  No vehicle instructions found. Click <strong>+ Add Instruction</strong> to create one.
                </td>
              </tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={i}>
                    {row.cells.map((cell, j) => (
                      <td
                        {...cell.getCellProps()}
                        key={j}
                        style={{ padding: "6px 8px", verticalAlign: "middle" }}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Button
            size="sm"
            variant="outline-secondary"
            className="py-0 px-2"
            style={{ fontSize: 11 }}
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            «
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="py-0 px-2"
            style={{ fontSize: 11 }}
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            ‹
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="py-0 px-2"
            style={{ fontSize: 11 }}
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            ›
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="py-0 px-2"
            style={{ fontSize: 11 }}
            onClick={() => gotoPage(pageOptions.length - 1)}
            disabled={!canNextPage}
          >
            »
          </Button>
          <span style={{ color: "#555" }}>
            Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length || 1}</strong>
          </span>
        </div>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          style={{ height: 26, fontSize: 11, padding: "0 6px" }}
          className="form-select form-select-sm"
        >
          {[10, 15, 25, 50].map((ps) => (
            <option key={ps} value={ps}>
              Show {ps}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default PageList_VehicleTypeInstructionMaster;
